import React, { useState, useMemo, useEffect, useRef } from "react";

/* ------------------------------------------------------------------ */
/*  ICONS — inline SVG, zero external deps                             */
/* ------------------------------------------------------------------ */

const iconBase = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

const Instagram = ({ size = 16, className = "" }) => (
  <svg {...iconBase} width={size} height={size} className={className}>
    <rect x="2" y="2" width="20" height="20" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.2" cy="6.8" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const Play = ({ size = 16, className = "", fill = "none" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className}>
    <path
      d="M7 4.5v15l14-7.5-14-7.5z"
      fill={fill}
      stroke={fill === "none" ? "currentColor" : "none"}
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

const Check = ({ size = 16, className = "" }) => (
  <svg {...iconBase} width={size} height={size} className={className}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const XIcon = ({ size = 16, className = "" }) => (
  <svg {...iconBase} width={size} height={size} className={className}>
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const ChevronDown = ({ size = 16, className = "" }) => (
  <svg {...iconBase} width={size} height={size} className={className}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const WhatsAppIcon = ({ size = 24, className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    className={className}
    fill="currentColor"
  >
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.91-7.01A9.86 9.86 0 0 0 12.04 2m0 1.67c2.24 0 4.35.87 5.94 2.46a8.23 8.23 0 0 1 2.43 5.85c0 4.56-3.71 8.27-8.27 8.27a8.3 8.3 0 0 1-4.22-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.23 8.23 0 0 1-1.26-4.4c0-4.56 3.71-8.32 8.27-8.32M8.53 7.4c-.16 0-.42.06-.64.31s-.85.83-.85 2.02.87 2.35.99 2.51c.12.16 1.7 2.72 4.2 3.7 2.08.82 2.5.66 2.96.62.46-.05 1.47-.6 1.68-1.18.21-.58.21-1.07.15-1.18-.06-.1-.23-.16-.48-.29-.25-.13-1.47-.73-1.7-.81-.23-.08-.39-.13-.56.13-.16.25-.64.81-.79.98-.14.16-.29.18-.54.06-.25-.13-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.39-1.72-.14-.25-.02-.39.11-.51.11-.11.25-.29.37-.44.12-.14.16-.25.25-.41.08-.16.04-.31-.02-.43-.06-.13-.55-1.36-.77-1.86-.2-.48-.4-.42-.56-.42z" />
  </svg>
);

const ArrowRight = ({ size = 16, className = "" }) => (
  <svg {...iconBase} width={size} height={size} className={className}>
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);

const Flame = ({ size = 16, className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    className={className}
    fill="currentColor"
    stroke="none"
  >
    <path d="M12 2c1 3-3 4-3 7.5A3.5 3.5 0 0 0 12 13a3.5 3.5 0 0 0 3-5.2C18.5 9 20 11.5 20 14a8 8 0 1 1-16 0c0-4.5 3.5-6 5-9 .3 1.5 1 2.5 2 3-.2-2.5.5-4.5 1-6z" />
  </svg>
);

const Clock = ({ size = 16, className = "" }) => (
  <svg {...iconBase} width={size} height={size} className={className}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

const Users = ({ size = 16, className = "" }) => (
  <svg {...iconBase} width={size} height={size} className={className}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const Zap = ({ size = 16, className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    className={className}
    fill="currentColor"
    stroke="none"
  >
    <path d="M13 2 3 14h7l-1 8 11-14h-7l1-6z" />
  </svg>
);

const ImagePlaceholderIcon = ({ size = 28, className = "" }) => (
  <svg {...iconBase} width={size} height={size} className={className}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="9" cy="9" r="2" />
    <path d="m21 15-5-5L5 21" />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  COACH PHOTO — paste your image URL (or import) here.               */
/*  Leave empty ("") to keep showing the placeholder frame.            */
/* ------------------------------------------------------------------ */
const COACH_PHOTO_SRC = "img/emiliano-sinfondo.webp";
const COACH_PHOTO_ALT = "Emiliano Kastro";

const CERT_PHOTO_SRC = "img/certificado.webp";
const CERT_PHOTO_ALT = "Certificado de Emiliano Kastro";

/* Navbar logo — paste your logo image URL here (e.g. "img/logo.png"
   after uploading it, or an external URL). Leave empty to keep the
   placeholder box + wordmark. */
const LOGO_SRC = "img/logo.webp";
const LOGO_ALT = "Emiliano Kastro";

/* Main hero video — hosted on Vimeo. Paste just the numeric video ID
   (the part after vimeo.com/, e.g. "824804225"). If your video is
   private/unlisted, you may also need the "h" hash parameter Vimeo
   gives you (looks like ?h=xxxxxxxxxx on the share link) — paste that
   into VIMEO_HASH too. Leave VIMEO_VIDEO_ID empty to keep the
   placeholder play-button. */
const VIMEO_VIDEO_ID = "1216990887";
const VIMEO_HASH = "";

/* ------------------------------------------------------------------ */
/*  DATA                                                               */
/* ------------------------------------------------------------------ */

const NAV_CTA = "#unirme";
const WA_BASE = "https://wa.me/5493804235595";
const waLink = (msg) => `${WA_BASE}?text=${encodeURIComponent(msg)}`;

const heroStats = [
  { value: "+USD 100.000", label: "facturados en 6 meses" },
  { value: "+8.000", label: "mentores activos en la comunidad" },
  { value: "N°1", label: "líder de ventas del equipo" },
];

const tickerItems = [
  "El Método EK",
  "+8.000 personas",
  "USD 30K · mes",
  "Sin filtros",
  "Resultados reales",
  "Top 1%",
  "Tu próximo capítulo",
  "Marketing High-Ticket",
];

const tickerAccents = [
  "text-amber-400",
  "text-[#E4D9BE]",
  "text-emerald-400",
  "text-rose-400",
];

const counters = [
  {
    value: "+100",
    suffix: "K",
    prefix: "USD ",
    label: "Facturados en los últimos 6 meses",
    accent: "orange",
  },
  {
    value: "212",
    suffix: "K",
    prefix: "USD ",
    label: "Facturación acumulada 2026",
    accent: "amber",
  },
  {
    value: "80",
    suffix: "%",
    prefix: "Hasta ",
    label: "De cada venta para vos",
    accent: "emerald",
  },
  {
    value: "12",
    suffix: " meses",
    prefix: "",
    label: "De acompañamiento post-venta",
    accent: "sky",
  },
  {
    value: "5",
    suffix: "",
    prefix: "",
    label: "Pilares del Método EK",
    accent: "rose",
  },
];

const accentMap = {
  orange: {
    text: "text-amber-300",
    bar: "bg-amber-500",
    ring: "ring-amber-500/30",
    soft: "bg-amber-500/10",
    border: "border-amber-700/40",
  },
  amber: {
    text: "text-amber-300",
    bar: "bg-amber-400",
    ring: "ring-amber-400/30",
    soft: "bg-amber-400/10",
    border: "border-amber-700/40",
  },
  emerald: {
    text: "text-emerald-300",
    bar: "bg-emerald-400",
    ring: "ring-emerald-400/30",
    soft: "bg-emerald-400/10",
    border: "border-emerald-700/40",
  },
  sky: {
    text: "text-[#E4D9BE]",
    bar: "bg-[#E4D9BE]",
    ring: "ring-[#E4D9BE]/30",
    soft: "bg-[#E4D9BE]/10",
    border: "border-[#E4D9BE]/30",
  },
  rose: {
    text: "text-rose-300",
    bar: "bg-rose-400",
    ring: "ring-rose-400/30",
    soft: "bg-rose-400/10",
    border: "border-rose-700/40",
  },
};

const journeyTimeline = [
  {
    n: "01",
    year: "Antes",
    title: "Comerciante de electrodomésticos y motos",
    body: "Un local al público, de todo un poco. Horas y horas de trabajo que nunca se traducían en ganancias reales, sin tiempo para mí ni para mi familia.",
    accent: "sky",
  },
  {
    n: "02",
    year: "El cambio",
    title: "Encontré a quien me abrió la puerta",
    body: "Buscando una alternativa, investigué el mundo de los negocios digitales y di con la persona que me inició en este sistema. Mi primer paso serio en lo digital.",
    accent: "amber",
  },
  {
    n: "03",
    year: "La apuesta",
    title: "Una inversión compartida, recuperada en días",
    body: "No tenía el dinero para arrancar solo, así que juntamos entre varios en casa. A los pocos días ya había recuperado lo invertido — después lo dupliqué, y luego lo tripliqué.",
    accent: "rose",
  },
  {
    n: "04",
    year: "Hoy",
    title: "Vivo 100% de mi negocio digital",
    body: "Me independicé del local. Me di los gustos que quería y, sobre todo, me regalé tiempo para mí y los míos. Hoy soy mentor de la comunidad.",
    accent: "orange",
  },
];

const skills = [
  {
    emoji: "📈",
    title: "Marketing de Afiliados",
    body: "Revendé productos digitales y ganá comisión por cada venta, sin crear nada desde cero.",
    featured: true,
  },
  {
    emoji: "🧠",
    title: "Marketing Digital",
    body: "Aprendé a captar clientes y crear contenido que convierte, en cualquier nicho.",
  },
  {
    emoji: "✨",
    title: "Marca Personal",
    body: "Construí un perfil que atraiga clientes ideales sin pagar publicidad.",
  },
  {
    emoji: "⚙️",
    title: "Automatización",
    body: "Sistemas y herramientas para que tu negocio funcione incluso cuando vos no estás.",
  },
  {
    emoji: "🧘",
    title: "Desarrollo Personal",
    body: "Mentalidad y hábitos para sostener resultados a largo plazo.",
  },
  {
    emoji: "🗂️",
    title: "Organización",
    body: "Métodos para gestionar tu tiempo y tu negocio sin caos.",
  },
  {
    emoji: "🎬",
    title: "Edición de Video",
    body: "Contenido audiovisual profesional para redes, hecho por vos mismo.",
  },
  {
    emoji: "🤝",
    title: "Monetización con Clientes",
    body: "Convertí tu conocimiento en servicios pagos para otros emprendedores.",
  },
];

const waMessages = [
  {
    name: "Nicolás",
    preview: "Emi, cerré mi segunda venta del mes 🔥 gracias por el empujón",
  },
  {
    name: "Camila",
    preview: "No lo puedo creer, recuperé la inversión en 9 días",
  },
  {
    name: "Federico",
    preview: "La llamada de ayer me cambió la cabeza. Vamos con todo",
  },
  { name: "Sofía", preview: "Mi primer cliente en USD, justo como decías 😭" },
];

const socialProofNotifications = [
  { name: "Juan Cruz", city: "Ushuaia", flag: "🇦🇷", plan: "Premium" },
  { name: "Martina", city: "Montevideo", flag: "🇺🇾", plan: "Experto" },
  { name: "Nicolás", city: "Rosario", flag: "🇦🇷", plan: "Premium" },
  { name: "Camila", city: "Punta del Este", flag: "🇺🇾", plan: "Esencial" },
  { name: "Federico", city: "Córdoba", flag: "🇦🇷", plan: "Experto" },
  { name: "Sofía", city: "Mendoza", flag: "🇦🇷", plan: "Premium" },
  { name: "Agustín", city: "Salta", flag: "🇦🇷", plan: "Esencial" },
  { name: "Valentina", city: "Colonia", flag: "🇺🇾", plan: "Premium" },
  { name: "Tomás", city: "Bariloche", flag: "🇦🇷", plan: "Experto" },
  { name: "Lucía", city: "Paysandú", flag: "🇺🇾", plan: "Premium" },
  { name: "Franco", city: "Neuquén", flag: "🇦🇷", plan: "Esencial" },
  { name: "Julieta", city: "Tucumán", flag: "🇦🇷", plan: "Premium" },
];

const planAccent = { Esencial: "sky", Premium: "orange", Experto: "emerald" };
const avatarGradient = {
  sky: "from-[#EFE6CC] to-[#C9B98A]",
  orange: "from-amber-500 to-amber-500",
  emerald: "from-emerald-400 to-emerald-600",
};

const fitYes = [
  "Querés generar ingresos usando solo tu celular y una conexión a internet.",
  "Querés aprender la habilidad mejor paga del mundo digital: el marketing.",
  "Buscás mejorar tu calidad de vida y la de tu familia.",
  "Tenés un empleo con un sueldo que no te alcanza para la vida que querés.",
  "Ya tenés un emprendimiento y querés potenciarlo al máximo.",
];

const fitNo = [
  "Esperás enriquecerte sin esfuerzo.",
  "No estás dispuesto a aprender algo nuevo.",
  "Buscás resultados mágicos e instantáneos.",
];

const planPricing = {
  esencial: { USD: 99, UYU: 3900, ARS: 77000, label: "Esencial" },
  premium: { USD: 179, UYU: 6900, ARS: 157000, label: "Premium" },
  experto: { USD: 199, UYU: 7900, ARS: 287000, label: "Experto" },
};

const currencySymbols = { ARS: "AR$", USD: "$", UYU: "$U" };

// Porcentaje de retención de la plataforma según el plan
const platformFeeRates = {
  esencial: 0.1, // 10% retenido -> 90% para el usuario
  premium: 0.15, // 15% retenido -> 85% para el usuario
  experto: 0.15, // 15% retenido -> 85% para el usuario
};

const plans = [
  {
    emoji: "🌱",
    name: "Esencial",
    tag: "90% de comisión",
    priceARS: "AR $77.000",
    priceOther: "USD 99 · $U 3.900",
    features: [
      "Marketing de Afiliados",
      "Comunidad que te acompaña y responde tus dudas",
      "Videos con tareas aplicables",
      "Clases en vivo",
      "Asistente virtual con soporte 24/7",
    ],
    note: "El punto de partida ideal para dar tus primeros pasos. Acceso por 1 año.",
    featured: false,
    accent: "sky",
  },
  {
    emoji: "🚀",
    name: "Premium",
    tag: "Elegido 🥈 · 85% de comisión",
    priceARS: "AR $157.000",
    priceOther: "USD 179 · $U 6.900",
    features: [
      "🎁 Paquete Esencial de regalo",
      "4 habilidades y pilares para aplicar",
      "Clases en vivo",
      "Sistema único de misiones",
      "Grupo de acompañamiento",
      "Asistente virtual con soporte 24/7",
      "Estrategias para maximizar resultados en menos tiempo",
    ],
    note: "Ideal para construir un negocio digital sólido con más herramientas. Acceso por 1 año.",
    featured: true,
    accent: "orange",
  },
  {
    emoji: "👑",
    name: "Experto",
    tag: "Sin Límites 🥇 · 85% de comisión",
    priceARS: "AR $287.000",
    priceOther: "USD 199 · $U 7.900",
    features: [
      "🎁 Paquetes Esencial y Premium de regalo",
      "Las 8 habilidades completas",
      "Más clases en vivo y más acompañamiento",
      "Videos con tareas aplicables",
      "Asistente virtual con soporte 24/7",
      "Clase en vivo de monetización con clientes",
      "Certificado oficial descargable",
      "Grupo exclusivo de monetización con clientes",
    ],
    note: "El camino completo, con certificación y máximo acompañamiento. Acceso por 1 año.",
    featured: false,
    accent: "emerald",
  },
];

const faqs = [
  {
    q: "¿Necesito experiencia previa para empezar?",
    a: "Absolutamente no. El programa está diseñado para llevarte de 0 a 100. Te enseño desde cómo crear tu cuenta hasta cerrar tu primera venta High-Ticket.",
  },
  {
    q: "Soy de Argentina, ¿puedo cobrar en dólares?",
    a: "Sí. Tengo mentores argentinos generando en moneda fuerte. Te enseño las plataformas exactas para retirar tu dinero sin problemas.",
  },
  {
    q: "Tengo poco tiempo libre, ¿es viable?",
    a: "Totalmente. Vos manejás tus horarios. Con 1 o 2 horas enfocadas al día ya tenés resultados.",
  },
  {
    q: "¿Qué pasa si me trabo o tengo dudas?",
    a: "Nunca estás solo. Tenés acceso a la comunidad privada y, según el plan, soporte directo por WhatsApp y Zooms semanales conmigo.",
  },
  {
    q: "¿Qué pasa si no vendo nada?",
    a: "El conocimiento que adquirís es tuyo para siempre. Aprendés marketing, ventas y estrategia digital, habilidades que aplicás para vender cualquier cosa online.",
  },
  {
    q: "¿Y si no me funciona como a los demás?",
    a: "Cada caso es único, pero el método es el mismo. Lo que diferencia a quienes facturan rápido es la consistencia, no el talento. Por eso te acompaño 12 meses post-venta.",
  },
  {
    q: "¿Necesito comprar el producto que voy a vender?",
    a: "No. Promocionás productos digitales ya creados y te llevás hasta el 90% de comisión según tu plan. Sin inventario, sin stock, sin envíos.",
  },
  {
    q: "¿Cuánto tarda en cerrarse mi primera venta?",
    a: "El promedio histórico de mis mentores activos es entre 14 y 30 días desde el inicio, según el tiempo que dediques.",
  },
];

/* ------------------------------------------------------------------ */
/*  SMALL HOOKS                                                        */
/* ------------------------------------------------------------------ */

/* Reveals content with a soft fade + rise once it scrolls into view.
   (Previously this drove a rapid 0→N digit count-up, which read as a
   flicker/glitch rather than a deliberate animation — a plain reveal
   reads calmer and more deliberate.) */
function useRevealOnView(threshold = 0.35) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return { ref, inView };
}

function Counter({ prefix, value, suffix, label, accent = "orange" }) {
  const { ref, inView } = useRevealOnView(0.35);
  const a = accentMap[accent];
  return (
    <div ref={ref} className="text-center px-4">
      <div
        className={`font-display text-4xl sm:text-5xl ${a.text} tracking-tight tabular-nums transition-all duration-700 ease-out ${
          inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
        }`}
      >
        {prefix}
        {value}
        {suffix}
      </div>
      <div className={`mx-auto mt-3 h-0.5 w-8 rounded-full ${a.bar}`} />
      <div className="mt-3 text-sm sm:text-base text-stone-400 uppercase tracking-wider">
        {label}
      </div>
    </div>
  );
}

/* Countdown to a rolling deadline — evergreen urgency window */
function useCountdown(hours = 2, minutes = 47) {
  const targetRef = useRef(Date.now() + (hours * 3600 + minutes * 60) * 1000);
  const [remaining, setRemaining] = useState(targetRef.current - Date.now());

  useEffect(() => {
    const id = setInterval(() => {
      const diff = targetRef.current - Date.now();
      if (diff <= 0) {
        targetRef.current = Date.now() + (hours * 3600 + minutes * 60) * 1000;
        setRemaining(targetRef.current - Date.now());
      } else {
        setRemaining(diff);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [hours, minutes]);

  const totalSeconds = Math.max(0, Math.floor(remaining / 1000));
  const hh = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
  const mm = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
  const ss = String(totalSeconds % 60).padStart(2, "0");
  return { hh, mm, ss };
}

/* Live-viewers ticker — small believable fluctuation */
function useLiveViewers(base = 41) {
  const [count, setCount] = useState(base);
  useEffect(() => {
    const id = setInterval(() => {
      setCount((c) => {
        const delta = Math.random() > 0.5 ? 1 : -1;
        const next = c + delta;
        return Math.min(63, Math.max(28, next));
      });
    }, 3200);
    return () => clearInterval(id);
  }, []);
  return count;
}

/* Cycles a queue of "recent purchase" toasts with randomized, believable
   timing. Pausable/dismissible; never repeats the same item twice in a row. */
function useSocialProofToast(
  items,
  { showMs = 5500, gapMin = 7000, gapMax = 13000, initialDelay = 3800 } = {},
) {
  const [toast, setToast] = useState(null);
  const [visible, setVisible] = useState(false);
  const lastIdx = useRef(-1);
  const timers = useRef([]);

  const clearTimers = () => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current = [];
  };

  const scheduleNext = (delay) => {
    const t = setTimeout(showNext, delay);
    timers.current.push(t);
  };

  const showNext = () => {
    let idx = Math.floor(Math.random() * items.length);
    if (items.length > 1 && idx === lastIdx.current) {
      idx = (idx + 1) % items.length;
    }
    lastIdx.current = idx;
    const minsAgo = Math.floor(Math.random() * 9) + 1;
    setToast({ ...items[idx], minsAgo, key: Date.now() });
    setVisible(true);
    const hideTimer = setTimeout(() => setVisible(false), showMs);
    timers.current.push(hideTimer);
    scheduleNext(showMs + 500 + gapMin + Math.random() * (gapMax - gapMin));
  };

  useEffect(() => {
    scheduleNext(initialDelay);
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    clearTimers();
    setVisible(false);
    scheduleNext(500 + gapMin + Math.random() * (gapMax - gapMin));
  };

  return { toast, visible, dismiss };
}

/* Talks to the Vimeo player iframe via postMessage, without any extra
   script tag: subscribes to play/pause events once the player reports
   "ready", and exposes a play()/pause() pair the UI can call. */
function useVimeoPlayPause(iframeRef, enabled) {
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const handleMessage = (e) => {
      if (typeof e.origin !== "string" || !e.origin.includes("vimeo.com"))
        return;
      let data;
      try {
        data = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
      } catch {
        return;
      }
      if (!data || !data.event) return;
      if (data.event === "ready") {
        const win = iframeRef.current && iframeRef.current.contentWindow;
        if (win) {
          win.postMessage(
            JSON.stringify({ method: "addEventListener", value: "play" }),
            "*",
          );
          win.postMessage(
            JSON.stringify({ method: "addEventListener", value: "pause" }),
            "*",
          );
        }
      } else if (data.event === "play") {
        setPlaying(true);
      } else if (data.event === "pause" || data.event === "ended") {
        setPlaying(false);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [enabled, iframeRef]);

  const send = (method) => {
    const win = iframeRef.current && iframeRef.current.contentWindow;
    if (win) win.postMessage(JSON.stringify({ method }), "*");
  };

  const toggle = () => send(playing ? "pause" : "play");

  return { playing, toggle };
}

/* ------------------------------------------------------------------ */
/*  MAIN APP                                                           */
/* ------------------------------------------------------------------ */

export default function App() {
  const [openFaq, setOpenFaq] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState("premium");
  const [salesPerWeek, setSalesPerWeek] = useState(4); // ahora es número de ventas totales (mensuales)
  const [currency, setCurrency] = useState("ARS");
  const [spotsLeft, setSpotsLeft] = useState(7);

  const { hh, mm, ss } = useCountdown(2, 47);
  const liveViewers = useLiveViewers(41);
  const {
    toast: proofToast,
    visible: proofVisible,
    dismiss: dismissProofToast,
  } = useSocialProofToast(socialProofNotifications);

  const vimeoIframeRef = useRef(null);
  const { playing: videoPlaying, toggle: toggleVideoPlayback } =
    useVimeoPlayPause(vimeoIframeRef, Boolean(VIMEO_VIDEO_ID));
  const [showPauseHint, setShowPauseHint] = useState(false);
  const pauseHintTimeout = useRef(null);

  const handleVideoOverlayClick = () => {
    toggleVideoPlayback();
    setShowPauseHint(true);
    if (pauseHintTimeout.current) clearTimeout(pauseHintTimeout.current);
    pauseHintTimeout.current = setTimeout(() => setShowPauseHint(false), 650);
  };

  // spots left ticks down slowly to feel alive, never below 3
  useEffect(() => {
    const id = setInterval(() => {
      setSpotsLeft((s) => (s > 3 ? s - 1 : 9));
    }, 45000);
    return () => clearInterval(id);
  }, []);

  const planData = planPricing[selectedPlan];
  const price = planData[currency];
  const symbol = currencySymbols[currency];
  const platformFee = platformFeeRates[selectedPlan]; // 0.10 o 0.15
  const userCommission = 1 - platformFee; // 0.90 o 0.85

  // CÁLCULO CORREGIDO: ahora usamos salesPerWeek como número TOTAL de ventas (sin multiplicar por 4.33)
  const { grossMonthly, netMonthly, commission, annual } = useMemo(() => {
    const totalSales = salesPerWeek; // es el número de ventas totales (mensuales)
    const gross = totalSales * price;
    const comm = gross * platformFee;
    const net = gross - comm;
    return {
      grossMonthly: gross,
      commission: comm,
      netMonthly: net,
      annual: net * 12,
    };
  }, [salesPerWeek, price, platformFee]);

  const fmt = (n) =>
    new Intl.NumberFormat("es-UY", { maximumFractionDigits: 0 }).format(
      Math.round(n),
    );

  // Ventas necesarias para recuperar la inversión
  const salesToRecover = Math.max(
    1,
    Math.ceil(price / (price * userCommission)),
  );

  const vimeoSrc = VIMEO_VIDEO_ID
    ? `https://player.vimeo.com/video/${VIMEO_VIDEO_ID}?title=0&byline=0&portrait=0&controls=0&dnt=1${
        VIMEO_HASH ? `&h=${VIMEO_HASH}` : ""
      }`
    : "";

  return (
    <div className="min-h-screen overflow-x-hidden bg-stone-950 text-stone-200 font-body selection:bg-amber-600 selection:text-white">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,500;0,600;0,700;0,800;1,600&family=Inter:wght@400;500;600;700;800&display=swap');
        html { font-size: 17.5px; }
        @media (min-width: 640px) { html { font-size: 18px; } }
        @media (min-width: 1024px) { html { font-size: 19px; } }
        .font-display { font-family: 'Playfair Display', Georgia, serif; letter-spacing: -0.015em; font-weight: 600; }
        .font-display-italic { font-family: 'Playfair Display', Georgia, serif; font-style: italic; font-weight: 500; }
        .font-body { font-family: 'Inter', sans-serif; }
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee { animation: marquee 28s linear infinite; }
        @keyframes emberGlow {
          0%, 100% { opacity: 0.55; transform: scale(1); }
          50% { opacity: 0.9; transform: scale(1.08); }
        }
        .animate-ember-glow { animation: emberGlow 4.5s ease-in-out infinite; }
        @keyframes pulseDot {
          0%, 100% { box-shadow: 0 0 0 0 rgba(244,63,94,0.6); }
          70% { box-shadow: 0 0 0 6px rgba(244,63,94,0); }
        }
        .pulse-dot { animation: pulseDot 1.8s ease-out infinite; }
        @keyframes ctaGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.45), 0 8px 40px rgba(16,185,129,0.35); }
          50% { box-shadow: 0 0 0 14px rgba(16,185,129,0), 0 8px 40px rgba(16,185,129,0.35); }
        }
        .cta-glow { animation: ctaGlow 2.4s ease-out infinite; }
        .clip-diagonal { clip-path: polygon(0 3%, 100% 0, 100% 97%, 0 100%); }
        .hairline { background: linear-gradient(90deg, transparent, rgba(245,158,11,0.5), transparent); height: 1px; }
        .hairline-gold { background: linear-gradient(90deg, transparent, rgba(245,158,11,0.6), transparent); height: 1px; }
        input[type="range"] { -webkit-appearance: none; appearance: none; height: 6px; border-radius: 9999px; background: linear-gradient(90deg,#E8730C,#F59E0B); }
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none; appearance: none; width: 20px; height: 20px; border-radius: 9999px;
          background: #FCD34D; border: 3px solid #0C0906; cursor: pointer; box-shadow: 0 0 0 3px rgba(245,158,11,0.3);
        }
        input[type="range"]::-moz-range-thumb {
          width: 20px; height: 20px; border-radius: 9999px; background: #FCD34D; border: 3px solid #0C0906; cursor: pointer;
        }
        @keyframes goldSweep {
          0% { background-position: 130% 0; }
          100% { background-position: -30% 0; }
        }
        .gold-sweep {
          background-image: linear-gradient(115deg, transparent 30%, rgba(251,191,36,0.16) 48%, transparent 66%);
          background-size: 220% 100%;
          animation: goldSweep 4.5s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .gold-sweep { animation: none; }
          .proof-toast-anim { transition: opacity 0.2s linear !important; transform: none !important; }
        }
      `}</style>

      {/* ---------------- URGENCY BAR ---------------- */}
      <div className="fixed top-0 inset-x-0 z-50 bg-gradient-to-r from-stone-950 via-stone-900 to-stone-950 text-stone-100 border-b border-amber-500/25 shadow-[0_4px_24px_rgba(0,0,0,0.45)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 h-16 sm:h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="pulse-dot absolute inline-flex h-full w-full rounded-full bg-rose-500" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500" />
            </span>
            <Zap
              size={18}
              className="shrink-0 text-amber-400 hidden sm:block"
            />
            <span className="truncate font-semibold text-base sm:text-lg tracking-tight text-amber-100">
              CUPOS DE ADMISIÓN ABIERTOS
              <span className="hidden sm:inline font-normal text-stone-400">
                {" "}
                — quedan solo
              </span>
            </span>
            <span className="shrink-0 font-display text-xl sm:text-xl bg-amber-500/15 border border-amber-400/30 text-amber-200 rounded-full px-3 leading-6">
              {spotsLeft}
            </span>
            <span className="hidden md:inline font-normal text-stone-400 text-base">
              lugares este mes
            </span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0 font-display tracking-wide text-lg sm:text-xl bg-black/40 border border-amber-500/20 text-amber-200 rounded-full px-3 py-1">
            <Clock size={16} className="text-amber-400" />
            <span className="tabular-nums">
              {hh}:{mm}:{ss}
            </span>
          </div>
        </div>
      </div>

      {/* ---------------- NAV ---------------- */}
      <header className="fixed top-16 sm:top-14 inset-x-0 z-40 backdrop-blur-md bg-stone-950/70 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {LOGO_SRC ? (
              <img
                src={LOGO_SRC}
                alt={LOGO_ALT}
                className="h-14 sm:h-15 w-auto object-contain"
              />
            ) : (
              <>
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg border border-dashed border-amber-500/40 bg-stone-900 flex items-center justify-center shrink-0">
                  <ImagePlaceholderIcon size={18} className="text-stone-600" />
                </div>
                <span className="font-display text-2xl tracking-wide text-stone-100">
                  EMILIANO
                  <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500 bg-clip-text text-transparent">
                    KASTRO
                  </span>
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-4">
            <nav className="hidden md:flex items-center gap-6 text-base text-stone-400">
              <a href="#metodo" className="hover:text-amber-400 transition">
                El Método
              </a>
              <a href={NAV_CTA} className="hover:text-amber-400 transition">
                Precios
              </a>
            </nav>
            <a
              href="https://www.instagram.com/emilianokastro/"
              target="_blank"
              rel="noreferrer"
              className="hidden sm:flex items-center gap-2 text-base text-stone-400 hover:text-amber-400 transition"
            >
              <Instagram size={16} />
              @emilianokastro
            </a>
            <a
              href={NAV_CTA}
              className="px-4 py-2 rounded-full bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 hover:from-amber-300 hover:via-amber-400 hover:to-amber-500 text-stone-950 text-base font-semibold transition shadow-[0_4px_20px_rgba(245,158,11,0.35)]"
            >
              Empezar ahora
            </a>
          </div>
        </div>
      </header>

      {/* ---------------- HERO ---------------- */}
      <section className="relative pt-48 pb-20 sm:pt-56 sm:pb-28 px-5 sm:px-8 overflow-hidden">
        <div
          className="absolute -top-40 right-0 w-[36rem] h-[36rem] rounded-full bg-amber-600/20 blur-3xl animate-ember-glow pointer-events-none"
          aria-hidden="true"
        />
        <div
          className="absolute top-40 -left-32 w-[28rem] h-[28rem] rounded-full bg-emerald-600/10 blur-3xl pointer-events-none"
          aria-hidden="true"
        />
        <div
          className="absolute bottom-0 left-1/3 w-[24rem] h-[24rem] rounded-full bg-amber-500/10 blur-3xl pointer-events-none"
          aria-hidden="true"
        />
        <div className="relative max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-[1fr_460px] gap-12 lg:gap-14 items-center">
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-8">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-stone-900 border border-amber-800/40 text-sm sm:text-base text-amber-400">
                  <Flame size={14} />
                  Últimos 6 meses: USD +100.000 facturados
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-rose-950/50 border border-rose-800/40 text-sm sm:text-base text-rose-300">
                  <span className="relative flex h-2 w-2">
                    <span className="pulse-dot absolute inline-flex h-full w-full rounded-full bg-rose-500" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500" />
                  </span>
                  {liveViewers} personas viendo esta página ahora
                </div>
              </div>

              <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl leading-[0.95] text-stone-50">
                De Comerciante
                <br />a{" "}
                <span className="bg-gradient-to-r from-amber-400 via-amber-400 to-amber-500 bg-clip-text text-transparent">
                  Empresario
                </span>{" "}
                Digital
              </h1>

              <p className="mt-6 max-w-xl text-lg sm:text-xl text-stone-400">
                Este video puede cambiar tu estabilidad financiera. Te cuento
                cómo pasé de tener un local de electrodomésticos y motos que no
                me dejaba ganancias reales, a vivir 100% de un negocio digital
                que manejo desde el celular.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
                <a
                  href={NAV_CTA}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 hover:from-amber-300 hover:via-amber-400 hover:to-amber-500 text-stone-950 font-semibold transition shadow-[0_8px_30px_rgba(245,158,11,0.35)]"
                >
                  Reservar mi cupo
                  <ArrowRight size={18} />
                </a>
                <span className="text-sm text-stone-500">
                  Acceso inmediato · Sin tarjeta · Plan adaptado a vos
                </span>
              </div>
            </div>

            {/* Coach photo — placeholder until COACH_PHOTO_SRC is set */}
            <div className="mx-auto w-full">
              {COACH_PHOTO_SRC ? (
                <img
                  src={COACH_PHOTO_SRC}
                  alt={COACH_PHOTO_ALT}
                  className="w-full h-auto max-h-[560px] object-cover"
                />
              ) : (
                <div className="w-full aspect-[3/4] flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-stone-800 via-stone-900 to-stone-950 text-stone-600">
                  <ImagePlaceholderIcon size={40} className="text-stone-600" />
                  <span className="text-sm text-stone-500 text-center px-6 leading-relaxed">
                    Foto de Emiliano Kastro
                    <br />
                    <span className="text-stone-600">
                      (reemplazar COACH_PHOTO_SRC)
                    </span>
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Video block */}
          <div className="mt-16 relative rounded-2xl overflow-hidden border border-white/10 bg-stone-900 aspect-video max-w-3xl group">
            {VIMEO_VIDEO_ID ? (
              <>
                <iframe
                  ref={vimeoIframeRef}
                  src={vimeoSrc}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                  allowFullScreen
                  title="Video de Emiliano Kastro"
                />
                {/* Transparent tap target: hides all native Vimeo controls
                    (via controls=0 above) and exposes play/pause only. */}
                <button
                  type="button"
                  onClick={handleVideoOverlayClick}
                  aria-label={
                    videoPlaying ? "Pausar video" : "Reproducir video"
                  }
                  className="absolute inset-0 w-full h-full bg-transparent border-0 cursor-pointer"
                >
                  <span
                    className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
                      !videoPlaying || showPauseHint
                        ? "opacity-100"
                        : "opacity-0"
                    }`}
                  >
                    <span className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center shadow-[0_0_30px_rgba(0,0,0,0.45)]">
                      {videoPlaying ? (
                        <span className="flex items-center gap-1.5">
                          <span className="w-2 h-7 sm:h-8 bg-white rounded-sm" />
                          <span className="w-2 h-7 sm:h-8 bg-white rounded-sm" />
                        </span>
                      ) : (
                        <Play
                          size={28}
                          className="text-white ml-1"
                          fill="white"
                        />
                      )}
                    </span>
                  </span>
                </button>
              </>
            ) : (
              <>
                <div className="absolute inset-0 bg-gradient-to-br from-amber-700/30 via-stone-950 to-emerald-900/10" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-amber-500 to-amber-500 flex items-center justify-center group-hover:scale-110 transition shadow-[0_0_40px_rgba(245,158,11,0.5)]">
                    <Play size={28} className="text-white ml-1" fill="white" />
                  </div>
                </div>
                <span className="absolute bottom-4 left-4 text-sm text-stone-400">
                  Subí el volumen — mi historia en 3 minutos
                </span>
                <span className="absolute top-4 right-4 text-[12px] text-stone-600 bg-black/30 px-2 py-1 rounded-full">
                  (pegar VIMEO_VIDEO_ID)
                </span>
              </>
            )}
          </div>

          {/* Stats row */}
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-3xl">
            {heroStats.map((s, i) => (
              <div key={s.label}>
                <div
                  className={`font-display text-3xl ${["text-amber-300", "text-amber-300", "text-emerald-300"][i]}`}
                >
                  {s.value}
                </div>
                <div className="text-sm text-stone-500 mt-1 uppercase tracking-wide">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- TICKER ---------------- */}
      <div className="border-y border-amber-800/40 bg-stone-900 py-4 overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee">
          {[...tickerItems, ...tickerItems, ...tickerItems].map((item, i) => (
            <span
              key={i}
              className="mx-4 font-display text-xl sm:text-2xl text-stone-600 flex items-center gap-4"
            >
              {item}{" "}
              <span className={tickerAccents[i % tickerAccents.length]}>✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* ---------------- COUNTERS ---------------- */}
      <section className="py-16 sm:py-20 px-5 sm:px-8 bg-stone-950">
        <div className="max-w-6xl mx-auto grid grid-cols-2 sm:grid-cols-5 gap-y-10 gap-x-4">
          {counters.map((c) => (
            <Counter
              key={c.label}
              prefix={c.prefix}
              value={c.value}
              suffix={c.suffix}
              label={c.label}
              accent={c.accent}
            />
          ))}
        </div>
      </section>

      {/* ---------------- HISTORIA ---------------- */}
      <section className="py-20 sm:py-28 px-5 sm:px-8 bg-stone-900 relative overflow-hidden">
        <div
          className="absolute top-0 right-0 w-96 h-96 rounded-full bg-amber-600/10 blur-3xl pointer-events-none"
          aria-hidden="true"
        />
        <div className="relative max-w-6xl mx-auto">
          <p className="text-amber-500 text-base uppercase tracking-widest mb-3">
            · Mi Historia ·
          </p>
          <h2 className="font-display text-4xl sm:text-5xl text-stone-50 max-w-2xl">
            Así fue mi camino
          </h2>

          <div className="mt-10 relative rounded-3xl border border-white/10 bg-gradient-to-br from-stone-800/60 to-stone-900 p-8 sm:p-10 max-w-3xl overflow-hidden">
            <span
              className="absolute -top-4 -left-2 font-display text-8xl text-amber-600/20 select-none"
              aria-hidden="true"
            >
              "
            </span>
            <p className="relative text-xl sm:text-2xl text-stone-200 leading-relaxed">
              Tenía un local de electrodomésticos y motos en el pueblo. Muchas
              horas, poco margen, y casi nada de tiempo para mi familia. Hoy
              facturo más desde el celular de lo que facturaba en el local. Y no
              fue suerte. Fue decisión.
            </p>
            <div className="relative mt-6 flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center font-display text-sm text-stone-950 shrink-0">
                EK
              </span>
              <span className="text-base text-stone-400">Emiliano Kastro</span>
            </div>
          </div>

          <div className="mt-6 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-700/40 text-sm sm:text-base text-emerald-300">
            <Zap size={14} />
            <span className="font-display text-lg text-emerald-300">
              x3 en días
            </span>
            <span className="text-emerald-300/70">
              — inversión recuperada y triplicada
            </span>
          </div>

          {/* Timeline */}
          <div className="mt-20 relative">
            <div
              className="hidden sm:block absolute top-[15px] left-[6%] right-[6%] h-px"
              style={{
                background:
                  "linear-gradient(to right, rgba(56,189,248,0.5), rgba(251,191,36,0.5), rgba(251,113,133,0.5), rgba(245,158,11,0.5))",
              }}
              aria-hidden="true"
            />
            <div className="grid sm:grid-cols-4 gap-8">
              {journeyTimeline.map((t) => {
                const a = accentMap[t.accent];
                return (
                  <div key={t.n} className="relative">
                    <div className="flex items-center gap-3 sm:block">
                      <span
                        className={`relative z-10 w-8 h-8 rounded-full ${a.soft} border ${a.border} flex items-center justify-center font-display text-sm ${a.text} shrink-0`}
                      >
                        {t.n}
                      </span>
                      <span className="sm:hidden text-sm text-stone-500 uppercase tracking-wide">
                        {t.year}
                      </span>
                    </div>
                    <span className="hidden sm:block mt-4 text-sm text-stone-500 uppercase tracking-wide">
                      {t.year}
                    </span>
                    <h4 className="font-semibold text-stone-100 mt-2">
                      {t.title}
                    </h4>
                    <p className="text-base text-stone-500 mt-2 leading-relaxed">
                      {t.body}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <div className="hairline-gold max-w-4xl mx-auto" />

      {/* ---------------- NO ES MAGIA. ES MÉTODO. ---------------- */}
      <section className="py-24 sm:py-32 px-5 sm:px-8 bg-stone-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-800/5 via-transparent to-amber-800/5 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto text-center">
          <p className="text-emerald-500 text-sm uppercase tracking-widest mb-6">
            · Mi Historia ·
          </p>
          <h2 className="font-display text-4xl sm:text-6xl leading-[1.05]">
            <span className="text-stone-50">NO ES MAGIA. </span>
            <span className="bg-gradient-to-r from-emerald-400 to-amber-400 bg-clip-text text-transparent">
              ES MÉTODO.
            </span>
          </h2>
          <p className="mt-8 text-xl sm:text-2xl text-stone-400 leading-relaxed max-w-3xl mx-auto">
            Tenía un local al público. Vendía de todo: electrodomésticos, motos.
            Y sinceramente, no me dejaba mis buenos ingresos. Nunca me
            alcanzaba. Hoy vivo 100% de esto, y sobre todo, me regalo tiempo a
            mí y a los míos. Y no fue suerte. Fue decisión.
          </p>
          <p className="mt-8 text-sm uppercase tracking-widest text-stone-600">
            — Emiliano Kastro, 45 años
          </p>
        </div>
      </section>

      {/* ---------------- CERTIFICADO ---------------- */}
      <section className="pb-20 sm:pb-28 px-5 sm:px-8 bg-stone-950">
        <div className="max-w-3xl mx-auto">
          {CERT_PHOTO_SRC ? (
            <img
              src={CERT_PHOTO_SRC}
              alt={CERT_PHOTO_ALT}
              className="w-full h-auto rounded-2xl border border-white/10"
            />
          ) : (
            <div className="w-full aspect-[4/3] rounded-2xl border border-white/10 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-stone-900 via-stone-900 to-stone-950 text-stone-600">
              <ImagePlaceholderIcon size={36} className="text-stone-600" />
              <span className="text-sm text-stone-500 text-center px-6 leading-relaxed">
                Foto del certificado
                <br />
                <span className="text-stone-600">
                  (reemplazar CERT_PHOTO_SRC)
                </span>
              </span>
            </div>
          )}
        </div>
      </section>

      {/* ---------------- SOBRE EMILIANO ---------------- */}
      <section id="metodo" className="py-20 sm:py-28 px-5 sm:px-8 bg-stone-950">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1fr_380px] gap-12 lg:gap-16 items-start">
          <div>
            <p className="text-emerald-500 text-sm uppercase tracking-widest mb-4">
              · Sobre Emiliano ·
            </p>
            <h2 className="font-display text-4xl sm:text-5xl leading-[1.05] text-stone-50">
              Quince años de comercio,{" "}
              <span className="text-amber-400">una</span>{" "}
              <span className="text-emerald-400">decisión</span> que lo cambió
              todo.
            </h2>
            <p className="mt-6 text-stone-400 leading-relaxed">
              Emiliano Kastro tiene 45 años y pasó la mayor parte de su vida
              detrás de un mostrador, vendiendo electrodomésticos y motos. Horas
              interminables, ingresos que nunca alcanzaban. Hoy lidera una
              comunidad de más de 8.000 mentores y vive 100% de los negocios
              digitales, sin depender de un local, un horario ni un jefe.
            </p>
            <p className="mt-4 text-stone-400 leading-relaxed">
              Su método no nació en una teoría: nació de la necesidad de
              recuperar tiempo, ingresos y libertad. Hoy lo enseña paso a paso a
              quienes están donde él estuvo.
            </p>
            <a
              href={NAV_CTA}
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-base font-semibold transition"
            >
              Conocer el sistema
              <ArrowRight size={15} />
            </a>
          </div>

          <div className="space-y-4">
            {[
              {
                title: "Líder de ventas N°1",
                body: "Reconocido dentro de la comunidad por su volumen de ventas y resultados sostenidos.",
              },
              {
                title: "+8.000 mentores formados",
                body: "Acompañó a cientos de personas a replicar el mismo sistema desde cero.",
              },
              {
                title: "+USD 100.000 facturados",
                body: "Solo en los últimos 6 meses, trabajando 100% desde el celular.",
              },
            ].map((c) => (
              <div
                key={c.title}
                className="rounded-2xl border border-white/10 hover:border-amber-500/30 p-6 transition-colors"
              >
                <h3 className="font-semibold text-stone-100">{c.title}</h3>
                <p className="text-base text-stone-500 mt-2 leading-relaxed">
                  {c.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- PLATAFORMA / HABILIDADES ---------------- */}
      <section className="py-20 sm:py-28 px-5 sm:px-8 bg-stone-950">
        <div className="max-w-6xl mx-auto">
          <p className="text-amber-500 text-base uppercase tracking-widest mb-3">
            · El Sistema ·
          </p>
          <h2 className="font-display text-4xl sm:text-5xl text-stone-50 max-w-2xl">
            8 habilidades para potenciarte
          </h2>
          <p className="mt-4 text-stone-500 max-w-xl">
            No son habilidades aisladas: es un sistema completo diseñado para
            ayudarte a generar resultados reales. Mi especialidad es Marketing
            de Afiliados — la vía más rápida para empezar.
          </p>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {skills.map((s) => (
              <div
                key={s.title}
                className={`rounded-2xl p-6 border ${
                  s.featured
                    ? "bg-gradient-to-br from-amber-600/15 to-amber-600/10 border-amber-700/60"
                    : "bg-stone-900 border-white/5"
                }`}
              >
                <span className="text-2xl">{s.emoji}</span>
                <h3 className="font-semibold text-stone-100 mt-4">{s.title}</h3>
                <p className="text-base text-stone-500 mt-2 leading-relaxed">
                  {s.body}
                </p>
                {s.featured && (
                  <span className="inline-block mt-4 text-[12px] uppercase tracking-wide text-amber-300 bg-amber-700/30 px-2 py-1 rounded-full">
                    Mi especialidad
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- WHATSAPP MESSAGES ---------------- */}
      <section className="py-20 sm:py-28 px-5 sm:px-8 bg-stone-950">
        <div className="max-w-6xl mx-auto">
          <p className="text-emerald-400 text-base uppercase tracking-widest mb-3">
            · Mensajes reales ·
          </p>
          <h2 className="font-display text-4xl sm:text-5xl text-stone-50 max-w-2xl">
            Lo que llega a mi WhatsApp.
          </h2>
          <p className="mt-4 text-stone-500 max-w-xl">
            Estos son solo algunos de los mensajes que recibo a diario...
          </p>

          <div
            className="mt-14 rounded-3xl border border-emerald-900/40 p-6 sm:p-10 relative overflow-hidden"
            style={{
              backgroundColor: "#0b141a",
              backgroundImage:
                "radial-gradient(rgba(16,185,129,0.08) 1px, transparent 1px)",
              backgroundSize: "18px 18px",
            }}
          >
            <div className="relative grid sm:grid-cols-2 gap-5">
              {waMessages.map((m, i) => (
                <div
                  key={m.name}
                  className={`relative ${i % 2 === 0 ? "sm:ml-0 sm:mr-8" : "sm:ml-8 sm:mr-0"}`}
                >
                  <div className="flex items-center gap-2 mb-1.5 px-1">
                    <span className="w-6 h-6 rounded-full bg-emerald-600/30 flex items-center justify-center text-[12px] font-semibold text-emerald-300 shrink-0">
                      {m.name[0]}
                    </span>
                    <span className="text-sm font-medium text-emerald-300">
                      {m.name}
                    </span>
                  </div>
                  <div className="relative bg-emerald-800/40 backdrop-blur-sm border border-emerald-700/30 rounded-2xl rounded-tl-sm px-4 py-3 shadow-lg">
                    <span
                      className="absolute -top-0 -left-2 w-3 h-3 bg-emerald-800/40 border-l border-t border-emerald-700/30"
                      style={{ clipPath: "polygon(100% 0, 0 0, 100% 100%)" }}
                      aria-hidden="true"
                    />
                    <p className="text-base text-stone-100 leading-relaxed">
                      {m.preview}
                    </p>
                    <div className="flex items-center justify-end gap-1 mt-2">
                      <span className="text-[12px] text-stone-400">
                        {m.time || "10:4" + (i + 1)}
                      </span>
                      <span className="flex -space-x-1.5 text-sky-400">
                        <Check size={12} />
                        <Check size={12} />
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- FILTRO DE ENTRADA ---------------- */}
      <section className="relative py-20 sm:py-28 px-5 sm:px-8 bg-stone-900 overflow-hidden">
        <div
          className="absolute top-10 left-0 w-80 h-80 rounded-full bg-emerald-600/10 blur-3xl pointer-events-none"
          aria-hidden="true"
        />
        <div
          className="absolute bottom-10 right-0 w-80 h-80 rounded-full bg-rose-600/10 blur-3xl pointer-events-none"
          aria-hidden="true"
        />
        <div className="relative max-w-6xl mx-auto">
          <p className="text-amber-500 text-base uppercase tracking-widest mb-3">
            · Filtro de entrada ·
          </p>
          <h2 className="font-display text-4xl sm:text-5xl text-stone-50 max-w-xl">
            ¿Será esto para vos?
          </h2>
          <p className="mt-4 text-stone-500 max-w-xl">
            No aceptamos a todo el mundo. Buscamos personas comprometidas.
          </p>

          <div className="mt-14 grid lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
            <div className="relative rounded-3xl border border-emerald-700/40 bg-gradient-to-b from-emerald-600/10 to-stone-900 p-8 sm:p-9 flex flex-col">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-11 h-11 rounded-2xl bg-emerald-500/15 border border-emerald-600/40 flex items-center justify-center shrink-0">
                  <Check size={20} className="text-emerald-400" />
                </span>
                <h3 className="font-display text-2xl text-stone-50">
                  Esto es para vos si:
                </h3>
              </div>
              <ul className="space-y-3">
                {fitYes.map((t) => (
                  <li
                    key={t}
                    className="flex items-start gap-3 text-base text-stone-300 rounded-xl bg-white/[0.03] hover:bg-emerald-500/[0.06] border border-white/5 px-4 py-3 transition"
                  >
                    <Check
                      size={15}
                      className="text-emerald-500 mt-0.5 shrink-0"
                    />
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative rounded-3xl border border-rose-900/40 bg-gradient-to-b from-rose-950/30 to-stone-900 p-8 sm:p-9 flex flex-col">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-11 h-11 rounded-2xl bg-rose-500/10 border border-rose-800/40 flex items-center justify-center shrink-0">
                  <XIcon size={20} className="text-rose-500" />
                </span>
                <h3 className="font-display text-2xl text-stone-50">
                  Esto NO es para vos si:
                </h3>
              </div>
              <ul className="space-y-3">
                {fitNo.map((t) => (
                  <li
                    key={t}
                    className="flex items-start gap-3 text-base text-stone-500 rounded-xl bg-white/[0.02] border border-white/5 px-4 py-3"
                  >
                    <XIcon
                      size={15}
                      className="text-rose-800 mt-0.5 shrink-0"
                    />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-8 rounded-2xl border border-amber-700/30 bg-amber-500/[0.06] px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-base text-stone-300">
              Si te reconociste en la columna verde, tu próximo paso está a un
              scroll de distancia.
            </p>
            <a
              href={NAV_CTA}
              className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 hover:from-amber-300 hover:via-amber-400 hover:to-amber-500 text-stone-950 text-base font-semibold transition"
            >
              Ver los planes
              <ArrowRight size={15} />
            </a>
          </div>
        </div>
      </section>

      {/* ---------------- PROYECCION / CALCULADORA ---------------- */}
      <section className="py-20 sm:py-28 px-5 sm:px-8 bg-stone-950">
        <div className="max-w-6xl mx-auto">
          <p className="text-amber-500 text-base uppercase tracking-widest mb-3">
            · Proyección personalizada ·
          </p>
          <h2 className="font-display text-4xl sm:text-5xl text-stone-50 max-w-xl">
            ¿Cuánto podrías generar?
          </h2>
          <p className="mt-4 text-stone-500 max-w-xl">
            Movés los controles y ves tu propio escenario. Los números son
            conservadores — la realidad de mis mentores top suele superarlos.
          </p>

          <div className="mt-14 grid lg:grid-cols-[1fr_1fr] gap-10 items-start">
            <div className="rounded-2xl bg-stone-900 border border-white/5 p-6 sm:p-8 space-y-8 min-w-0">
              <div>
                <label className="text-sm uppercase tracking-wide text-stone-500">
                  Moneda
                </label>
                <div className="mt-3 flex flex-wrap gap-2">
                  {Object.keys(currencySymbols).map((c) => (
                    <button
                      key={c}
                      onClick={() => setCurrency(c)}
                      className={`px-4 py-2 rounded-full text-base font-medium transition ${
                        currency === c
                          ? "bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 text-stone-950"
                          : "bg-stone-800/60 text-stone-400 hover:text-stone-200"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm uppercase tracking-wide text-stone-500">
                  Plan
                </label>
                <div className="mt-3 grid grid-cols-1 sm:flex sm:flex-wrap gap-2">
                  {Object.entries(planPricing).map(([key, p]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedPlan(key)}
                      className={`px-4 py-2 rounded-full text-base font-medium transition whitespace-nowrap ${
                        selectedPlan === key
                          ? "bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 text-stone-950"
                          : "bg-stone-800/60 text-stone-400 hover:text-stone-200"
                      }`}
                    >
                      {p.label} {symbol}
                      {fmt(p[currency])}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between gap-3">
                  <label className="text-sm uppercase tracking-wide text-stone-500">
                    Ventas estimadas (mensuales)
                  </label>
                  <span className="text-base text-amber-400 font-semibold shrink-0">
                    {salesPerWeek} ventas
                  </span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="20"
                  value={salesPerWeek}
                  onChange={(e) => setSalesPerWeek(Number(e.target.value))}
                  className="w-full mt-4"
                />
              </div>

              <div className="text-sm text-stone-600 leading-relaxed border-t border-white/5 pt-5">
                ¿Cómo se calcula? Revendés la misma membresía que tenés. Cada
                venta entra al precio del plan elegido. La plataforma retiene un{" "}
                <span className="text-amber-400 font-semibold">
                  {(platformFee * 100).toFixed(0)}%
                </span>{" "}
                de comisión, el{" "}
                <span className="text-emerald-400 font-semibold">
                  {(userCommission * 100).toFixed(0)}%
                </span>{" "}
                queda para vos.
              </div>
            </div>

            <div className="rounded-2xl bg-gradient-to-br from-emerald-700/15 via-stone-900 to-amber-700/10 border border-emerald-700/30 p-6 sm:p-8 min-w-0">
              <span className="text-sm uppercase tracking-wide text-stone-400">
                Tu ganancia mensual (neta del {platformFee * 100}%)
              </span>
              <div className="font-display text-3xl sm:text-5xl text-emerald-300 mt-2 break-words">
                {symbol}
                {fmt(netMonthly)}
              </div>
              <p className="text-sm text-stone-500 mt-2 break-words">
                Bruto {symbol}
                {fmt(grossMonthly)} − comisión plataforma {symbol}
                {fmt(commission)}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8 pt-8 border-t border-white/10">
                <div>
                  <span className="text-sm uppercase tracking-wide text-stone-500">
                    Proyección anual
                  </span>
                  <div className="font-display text-xl sm:text-2xl text-amber-300 mt-1 break-words">
                    {symbol}
                    {fmt(annual)}
                  </div>
                </div>
                <div>
                  <span className="text-sm uppercase tracking-wide text-stone-500">
                    Recuperás la inversión
                  </span>
                  <div className="font-display text-xl sm:text-2xl text-stone-100 mt-1 break-words">
                    con {salesToRecover} ventas
                  </div>
                  <span className="text-[12px] text-stone-600">
                    Aprox. {Math.ceil(salesToRecover / salesPerWeek)} semana(s)
                    a tu ritmo actual
                  </span>
                </div>
              </div>

              <a
                href={waLink(
                  `Hola Emi, ya vi la web y quiero entrar al Plan ${planData.label}. ¿Me pasás los datos para transferir en Pesos/Dólares?`,
                )}
                target="_blank"
                rel="noreferrer"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 hover:from-amber-300 hover:via-amber-400 hover:to-amber-500 text-stone-950 font-semibold transition text-center"
              >
                Reservar mi lugar en {planData.label}
                <ArrowRight size={18} />
              </a>
              <p className="text-[12px] text-stone-600 mt-3 text-center">
                Te abre WhatsApp con un mensaje pre-cargado para Emiliano.
              </p>
            </div>
          </div>

          <p className="mt-8 text-center text-sm text-stone-600">
            Promedio de mentores activos: USD 3.100/mes a los 90 días.
          </p>
        </div>
      </section>

      <div className="hairline-gold max-w-4xl mx-auto" />

      {/* ---------------- PLANES ---------------- */}
      <section
        id="unirme"
        className="py-20 sm:py-28 px-5 sm:px-8 bg-stone-900 scroll-mt-16"
      >
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
            <p className="text-amber-500 text-base uppercase tracking-widest">
              · Elegí tu nivel ·
            </p>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-950/40 border border-rose-800/40 text-[12px] text-rose-300">
              <Users size={12} />
              Solo quedan {spotsLeft} cupos este mes
            </div>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl text-stone-50 max-w-xl">
            Elegí cómo querés empezar
          </h2>
          <p className="mt-4 text-stone-500 max-w-xl">
            Todos los planes incluyen acceso al Método EK. Cuanto más
            acompañamiento elijas, más rápido avanzás.
          </p>

          {/* spots-left progress bar */}
          <div className="mt-6 max-w-md">
            <div className="h-2 rounded-full bg-stone-800 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-rose-500 via-amber-500 to-amber-400 transition-all"
                style={{ width: `${(spotsLeft / 12) * 100}%` }}
              />
            </div>
            <p className="text-[12px] text-stone-600 mt-2">
              Cupos de admisión limitados para mantener el acompañamiento
              personalizado.
            </p>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {plans.map((p) => {
              const a = accentMap[p.accent];
              return (
                <div
                  key={p.name}
                  className={`relative rounded-2xl p-7 flex flex-col ${
                    p.featured
                      ? "bg-gradient-to-b from-amber-700/25 via-stone-900 to-amber-700/10 border border-amber-600"
                      : "bg-stone-800/60 border border-white/5"
                  }`}
                >
                  {p.featured && (
                    <span className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 text-stone-950 text-[12px] font-semibold uppercase tracking-wide">
                      Más elegido
                    </span>
                  )}
                  <span className="text-2xl">{p.emoji}</span>
                  <h3 className="font-display text-2xl text-stone-50 mt-4">
                    {p.name}
                  </h3>
                  <span
                    className={`inline-block w-fit mt-1.5 text-[12px] font-semibold uppercase tracking-wide ${a.text}`}
                  >
                    {p.tag}
                  </span>
                  <div className="mt-3">
                    <div className={`text-2xl font-display ${a.text}`}>
                      {p.priceARS}
                    </div>
                    <div className="text-sm text-stone-500 mt-1">
                      {p.priceOther}
                    </div>
                  </div>
                  <ul className="mt-6 space-y-3 flex-1">
                    {p.features.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-sm text-stone-400"
                      >
                        <Check
                          size={14}
                          className={`${a.text} mt-0.5 shrink-0`}
                        />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-stone-600 mt-5">{p.note}</p>
                  <a
                    href={waLink(
                      `Hola Emi, ya vi la web y quiero entrar al Plan ${p.name}. ¿Me pasás los datos para transferir en Pesos/Dólares?`,
                    )}
                    target="_blank"
                    rel="noreferrer"
                    className={`mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-base font-semibold transition ${
                      p.featured
                        ? "bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 hover:from-amber-300 hover:via-amber-400 hover:to-amber-500 text-stone-950"
                        : "bg-white/5 hover:bg-white/10 text-stone-200"
                    }`}
                  >
                    Ingresar ahora
                    <ArrowRight size={15} />
                  </a>
                </div>
              );
            })}
          </div>

          <div className="mt-10 grid sm:grid-cols-2 gap-4 text-sm text-stone-500">
            <div className="rounded-xl border border-white/5 px-5 py-4">
              🇺🇾 Uruguay — Prex · Transferencia · MercadoPago · Tarjetas
            </div>
            <div className="rounded-xl border border-white/5 px-5 py-4">
              🇦🇷 Argentina — Prex · Transferencia · MercadoPago · Tarjetas
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- FAQ ---------------- */}
      <section className="py-20 sm:py-28 px-5 sm:px-8 bg-stone-950">
        <div className="max-w-3xl mx-auto">
          <p className="text-amber-500 text-base uppercase tracking-widest mb-3">
            · Antes de empezar ·
          </p>
          <h2 className="font-display text-4xl sm:text-5xl text-stone-50">
            Preguntas frecuentes
          </h2>
          <p className="mt-4 text-stone-500">
            Resolvemos tus dudas antes de que tomes la decisión.
          </p>

          <div className="mt-12 divide-y divide-white/5 border-t border-b border-white/5">
            {faqs.map((f, i) => (
              <div key={f.q}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 py-5 text-left"
                >
                  <span className="text-base sm:text-lg font-medium text-stone-100">
                    {f.q}
                  </span>
                  <ChevronDown
                    size={18}
                    className={`shrink-0 text-amber-500 transition-transform ${openFaq === i ? "rotate-180" : ""}`}
                  />
                </button>
                {openFaq === i && (
                  <p className="pb-6 text-base text-stone-400 leading-relaxed pr-8">
                    {f.a}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- CIERRE: TU PRÓXIMO CAPÍTULO ---------------- */}
      <section className="relative py-28 sm:py-36 px-5 sm:px-8 overflow-hidden">
        <div
          className="absolute inset-0 bg-gradient-to-br from-emerald-950 via-stone-950 to-amber-950/40"
          aria-hidden="true"
        />
        <div
          className="absolute top-0 left-1/4 w-[32rem] h-[32rem] rounded-full bg-emerald-600/20 blur-3xl pointer-events-none"
          aria-hidden="true"
        />
        <div
          className="absolute bottom-0 right-1/4 w-[28rem] h-[28rem] rounded-full bg-amber-600/15 blur-3xl pointer-events-none"
          aria-hidden="true"
        />

        {/* decorative floating chat bubbles */}
        <div className="hidden lg:flex absolute top-16 left-10 items-center gap-2 px-4 py-2 rounded-2xl rounded-bl-sm bg-emerald-800/30 border border-emerald-700/30 text-sm text-stone-200 shadow-lg backdrop-blur-sm">
          <WhatsAppIcon size={14} className="text-emerald-400" />
          "Dejé mi trabajo hoy 🔥"
        </div>
        <div className="hidden lg:flex absolute bottom-24 right-12 items-center gap-2 px-4 py-2 rounded-2xl rounded-br-sm bg-emerald-800/30 border border-emerald-700/30 text-sm text-stone-200 shadow-lg backdrop-blur-sm">
          <WhatsAppIcon size={14} className="text-emerald-400" />
          "Pagué mis deudas 🙌"
        </div>

        <div className="relative max-w-3xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-emerald-300 text-sm uppercase tracking-widest mb-6">
            <Zap size={13} />
            Tu próximo capítulo
          </span>
          <h2 className="font-display text-5xl sm:text-6xl text-stone-50 leading-[1.05]">
            Vení a escucharnos{" "}
            <span className="bg-gradient-to-r from-emerald-400 to-amber-400 bg-clip-text text-transparent">
              cara a cara
            </span>
          </h2>
          <p className="mt-6 text-stone-400 text-lg sm:text-xl max-w-lg mx-auto leading-relaxed">
            Lo que llega a mí por WhatsApp todos los días: mentores que dejaron
            su trabajo, pagaron deudas y se compraron su primer auto. Tu próximo
            capítulo empieza con un mensaje.
          </p>

          <a
            href={waLink("Hola Emiliano, vi tu web y quiero empezar!")}
            target="_blank"
            rel="noreferrer"
            className="cta-glow mt-10 inline-flex items-center justify-center gap-3 px-9 py-4 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-lg sm:text-xl font-semibold transition"
          >
            <WhatsAppIcon size={22} />
            Escribirme por WhatsApp
          </a>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-stone-500">
            <span className="flex items-center gap-1.5">
              <Users size={13} className="text-emerald-400" />
              +8.000 mentores ya escribieron
            </span>
            <span className="flex items-center gap-1.5">
              <Check size={13} className="text-emerald-400" />
              Respondo personalmente
            </span>
            <span className="flex items-center gap-1.5">
              <Clock size={13} className="text-emerald-400" />
              Respuesta el mismo día
            </span>
          </div>
        </div>
      </section>

      {/* ---------------- FOOTER ---------------- */}
      <footer className="py-14 px-5 sm:px-8 bg-stone-900 border-t border-white/5">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            {LOGO_SRC ? (
              <img
                src={LOGO_SRC}
                alt={LOGO_ALT}
                className="h-14 w-auto object-contain"
              />
            ) : (
              <>
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 flex items-center justify-center font-display text-base text-stone-950">
                  EK
                </div>
                <span className="font-display text-xl text-stone-200">
                  EMILIANO KASTRO
                </span>
              </>
            )}
          </div>
          <p className="text-sm text-stone-600">
            © 2026 · Todos los derechos reservados
          </p>
          <div className="flex items-center gap-5 text-sm text-stone-500">
            <a href="#" className="hover:text-stone-300 transition">
              Privacidad
            </a>
            <a href="#" className="hover:text-stone-300 transition">
              Términos
            </a>
            <a
              href="https://www.instagram.com/emilianokastro/"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 hover:text-amber-400 transition"
            >
              <Instagram size={14} />
              Instagram
            </a>
          </div>
        </div>
      </footer>

      {/* ---------------- SOCIAL PROOF TOAST ---------------- */}
      {proofToast && (
        <div
          role="status"
          aria-live="polite"
          className={`proof-toast-anim fixed bottom-5 sm:bottom-8 left-4 sm:left-8 z-40 w-[272px] sm:w-[310px] transition-all duration-500 ease-out ${
            proofVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-3 pointer-events-none"
          }`}
        >
          <div className="relative rounded-2xl p-[1px] bg-gradient-to-br from-amber-300/70 via-amber-500/25 to-amber-700/60 shadow-[0_16px_44px_rgba(0,0,0,0.5)]">
            <div className="gold-sweep relative rounded-[15px] bg-stone-900/95 backdrop-blur-xl px-4 py-3.5 pr-8 overflow-hidden border border-white/[0.03]">
              <button
                onClick={dismissProofToast}
                aria-label="Cerrar notificación"
                className="absolute top-2.5 right-2.5 text-stone-600 hover:text-stone-300 transition p-0.5"
              >
                <XIcon size={12} />
              </button>

              <div className="relative flex items-start gap-3">
                <span
                  className={`relative shrink-0 w-10 h-10 rounded-full bg-gradient-to-br ${
                    avatarGradient[planAccent[proofToast.plan]]
                  } flex items-center justify-center font-display text-base tracking-wide text-stone-950 ring-1 ring-white/20`}
                >
                  {proofToast.name
                    .split(" ")
                    .slice(0, 2)
                    .map((w) => w[0])
                    .join("")
                    .toUpperCase()}
                  <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-emerald-500 border-2 border-stone-900 flex items-center justify-center">
                    <Check size={9} className="text-white" strokeWidth={3} />
                  </span>
                </span>

                <div className="min-w-0">
                  <p className="text-base text-stone-100 leading-snug">
                    <span className="font-semibold">{proofToast.name}</span>
                    <span className="text-stone-400">
                      {" "}
                      de {proofToast.city} {proofToast.flag}
                    </span>
                  </p>
                  <p
                    className={`text-sm font-medium mt-0.5 ${
                      accentMap[planAccent[proofToast.plan]].text
                    }`}
                  >
                    Adquirió la Membresía {proofToast.plan}
                  </p>
                  <p className="text-[12px] text-stone-500 mt-1 flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                    Hace {proofToast.minsAgo} min · Compra verificada
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- FLOATING WHATSAPP ---------------- */}
      <a
        href={waLink("Hola Emiliano, vi tu web y quiero empezar!")}
        target="_blank"
        rel="noreferrer"
        className="fixed bottom-5 sm:bottom-8 right-5 sm:right-8 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 flex items-center justify-center shadow-[0_8px_30px_rgba(16,185,129,0.45)] transition"
        aria-label="Escribir por WhatsApp"
      >
        <WhatsAppIcon size={26} className="text-white" />
      </a>
    </div>
  );
}
